import { Button } from '@mui/material';
import { toast } from '@side/pantry';
import { useRouter } from 'next/router';
import { useFormContext } from 'react-hook-form';
import { useUnsavedChangesContext } from '@/components/UnsavedChanges';
import { TrackOfferEvent } from '$analytics/useTrackAnalytics/domains/offers/events';
import { useTrackOfferAnalytics } from '$analytics/useTrackAnalytics/domains/offers/useTrackOfferAnalytics';
import type { CoreRequest } from '$api-clients/hooks';
import {
  BuyerAgreementStatus,
  useBuyerAgreementStatus,
} from '../hooks/useBuyerAgreementStatus';
import { useBuyerStepSubmit } from '../hooks/useBuyerStepSubmit';
import { BuyerMethod, type CreateOfferFormData } from '../types';

type Offer = CoreRequest<'/offers/{offerId}'>['Data'];

enum ButtonLabels {
  DEFAULT = 'Create Offer',
  NEW_BUYER = 'Create Offer & Buyer',
  PREPARE_AGREEMENT = 'Prepare Buyer Agreement',
}

export const OfferSubmitButton = () => {
  const {
    getValues,
    handleSubmit,
    formState: { isValid },
  } = useFormContext<CreateOfferFormData>();
  const values = getValues();
  const agreementStatus = useBuyerAgreementStatus();
  const { push } = useRouter();
  const { onSubmit, isLoading } = useBuyerStepSubmit();
  const { setIsPageDirty } = useUnsavedChangesContext();
  const trackOfferEvent = useTrackOfferAnalytics();

  const existingBuyerReady =
    values.buyerMethod === BuyerMethod.existingBuyer && values.buyer;
  const agreementDataReady =
    values.buyerMethod === BuyerMethod.existingBuyer &&
    values.buyer?.startDate &&
    values.buyer.endDate &&
    values.buyer.commissionValue;

  const trackOffer = (offer: Offer) => {
    trackOfferEvent(TrackOfferEvent.ClickCreateOfferConfirmation, {
      interactionId: offer.id,
      isExistingBuyer: values.buyerMethod === BuyerMethod.existingBuyer,
      clientGroupId: offer.clientGroupId,
    });
  };

  const onSubmitOffer = handleSubmit(async () => {
    setIsPageDirty(false);
    const offer = await onSubmit();
    if (!offer) {
      toast('error', 'Failed to create offer');
      setIsPageDirty(true);
      return;
    }

    trackOffer(offer);
    void push(`/offers/${offer.id}/cover-sheet`);
  });

  const onSubmitOfferAndRouteToBuyer = handleSubmit(async () => {
    setIsPageDirty(false);
    const offer = await onSubmit();
    if (!offer) {
      toast('error', 'Failed to create offer');
      setIsPageDirty(true);
      return;
    }

    trackOffer(offer);

    // Users can only visit the documents page if the buyer agreement data is set.
    const path = agreementDataReady
      ? `/buyers/${offer.clientGroupId}/documents`
      : `/buyers/${offer.clientGroupId}/buyer-agreement`;

    await push(path);
    toast('success', 'Offer created and linked to Buyer');
  });

  // Default state - No buyer selected
  const props = {
    disabled: !isValid,
    onClick: onSubmitOffer,
    children: ButtonLabels.DEFAULT,
  };

  // Determine button label
  if (values.buyerMethod === BuyerMethod.newBuyer) {
    // New Buyer Selected
    props.children =
      values.type === 'envelope'
        ? ButtonLabels.PREPARE_AGREEMENT
        : ButtonLabels.NEW_BUYER;
  } else if (
    existingBuyerReady &&
    agreementStatus === BuyerAgreementStatus.NOT_SENT &&
    values.type === 'envelope'
  ) {
    // Existing Buyer Selected
    props.children = ButtonLabels.PREPARE_AGREEMENT;
  }

  // Determine if alternate button callback
  if (props.children === ButtonLabels.PREPARE_AGREEMENT) {
    props.onClick = onSubmitOfferAndRouteToBuyer;
  }

  return (
    <Button size="large" variant="contained" loading={isLoading} {...props} />
  );
};
